import React, { useEffect, useState } from "react";
import { Terminal } from "lucide-react";

/**
 * CodeWindow
 * Small floating terminal-style card displaying AIX commands typing
 * progressively. Rotates through a few snippets to add a sense of liveness.
 */
const SNIPPETS = [
  [
    { p: "$", t: "lspv | head -3" },
    { o: "hdisk0   00f6e0a4d8a9    rootvg     active" },
    { o: "hdisk1   00f6e0a4d8b1    datavg     active" },
    { o: "hdisk2   00f6e0a4d8c4    backupvg   active" }
  ],
  [
    { p: "$", t: "clstat -o" },
    { o: "Cluster:  bw-prod   State: STABLE" },
    { o: "Node A:   active   Resource: app_rg ONLINE" },
    { o: "Node B:   standby  Resource: app_rg OFFLINE" }
  ],
  [
    { p: "$", t: "lparstat -i | grep Entitled" },
    { o: "Entitled Capacity:  3.20" },
    { o: "Variable Capacity Weight: 128" },
    { o: "Mode: Uncapped" }
  ]
];

export default function CodeWindow({ className = "" }) {
  const [snipIdx, setSnipIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [showOutput, setShowOutput] = useState(false);

  useEffect(() => {
    const snippet = SNIPPETS[snipIdx];
    const cmd = snippet[0].t;
    setTyped("");
    setShowOutput(false);
    let i = 0;
    const typer = setInterval(() => {
      i++;
      setTyped(cmd.slice(0, i));
      if (i >= cmd.length) {
        clearInterval(typer);
        setTimeout(() => setShowOutput(true), 200);
      }
    }, 55);

    const next = setTimeout(() => {
      setSnipIdx((v) => (v + 1) % SNIPPETS.length);
    }, 5500);

    return () => {
      clearInterval(typer);
      clearTimeout(next);
    };
  }, [snipIdx]);

  const snippet = SNIPPETS[snipIdx];

  return (
    <div
      className={`rounded-xl bg-slate-950/95 backdrop-blur border border-slate-800 shadow-2xl shadow-slate-900/40 overflow-hidden ${className}`}
    >
      {/* Title bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-slate-900/60">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-500/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
        <span className="ml-2 inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-400">
          <Terminal className="h-3 w-3" /> aix7@bw-node-a
        </span>
      </div>

      {/* Body */}
      <div className="px-4 py-3 font-mono text-[11.5px] leading-[1.55] text-brand-300 min-h-[120px]">
        <div>
          <span className="text-accentc-400">{snippet[0].p}</span>{" "}
          <span className="text-slate-100">{typed}</span>
          <span className="inline-block h-3.5 w-1.5 align-middle ml-0.5 bg-brand-300 animate-pulse" />
        </div>
        {showOutput &&
          snippet.slice(1).map((line, i) => (
            <div key={i} className="text-slate-300/90 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              {line.o}
            </div>
          ))}
      </div>
    </div>
  );
}
